<template lang="pug">
.k-kintell-card-widget.fill-height(:class="classes")
  k-kintell-card-modal-share(
    v-if="shareButton"
    v-model="showShareModal"
    :kintellCard="card"
    title="Check out my Kintell Card!"
    text="Check out my Kintell Card!"
  )
  k-card(:to="card.link" box-shadow="small").fill-height
    k-icon(v-if="card.wistiaId" color="brand-dark-blue" size="27").k-kintell-card-widget__video video
    div(v-if="card.stats.favouriteCount").k-kintell-card-widget__favourite
      k-kintell-card-favourite-count(
        :value="card.stats.favouriteCount"
      )
    k-card-text(small).text-xs-center.k-kintell-card-widget__content
      .text-xs-center
        k-user-avatar(:src="advisor.avatarUrl" :size="small ? 83 : 99").k-kintell-card-widget__avatar
      .k-kintell-card-widget__title(:class="small ? 'paragraph--book' : 'paragraph'")
        .brand-dark-blue--text.pb-1 {{ advisor.fullName }}
        .brand-pink--text.font-weight-bold.break-word {{ card.title || 'Draft' }}
      div(v-if="!hideReviews && card.stats.reviewsCount").d-flex.align-center.justify-center.k-kintell-card-widget__rating.brand-dark-aqua--text.mt-5
        k-rating(
          :value="card.stats.reviewsAverage"
          :star-size="14"
          half-increments
        ).ma-0.px-2.small-caps--small
        .small-caps--small {{ card.stats.reviewsCount }}

      // chip that shows private card
      k-chip(v-if="card.isPrivate" color="brand-pink" text-color="white").no-pointer.mt-5 private
      k-chip.k-kintell-card-widget__status(
        v-else-if="shouldSeeCardStatus"
        color="brand-dark-blue"
        small
      ).white--text.mt-5 {{ card.friendlyStatus }}

      k-kintell-card-skill-list(:skills="skills" v-if="!minimal && skills.length && card.status !== 'unlisted'").mt-7.mb-3
    slot(name="footer" v-if="!hideFooter")
      k-card-text(small).k-kintell-card-widget__actions.pa-0
        v-row(align="center").ma-0.py-3.px-4
          v-col(v-if="hasFooterButtons" align-center justify-start ).d-flex.pa-0
            .kintell-cards__footer-buttons
              k-button-outline(x-small v-if="editDetails" color="brand-dark-blue" :to="card.editLink").ma-0 Edit
              k-button-outline(x-small v-if="removeButton" color="brand-dark-blue" @click.prevent="removeButtonClicked").ma-0 Remove
              k-icon(
                v-if="shareButton && (card.status === 'published' || card.isPrivate)"
                size="17"
                @click.prevent="showShareModal = true"
                color="brand-dark-blue"
              ).pl-3 share
          v-col(:class="`text-xs-${minimal ? 'center' : 'right'}`").pa-0
            template(v-if="!card.isFree")
              span.k-kintell-card-widget__rate.font-weight-bold  {{ card.convertedRate | dollarsWithoutCents(usersCurrency) }}
              span.paragraph--small  / 30mins
            template(v-else)
              span.paragraph--small.font-weight-bold FREE
</template>

<script>
import { UsesAuthenticatedUser, UsesCurrency } from '@/mixins'
import { take } from 'lodash'
import { KintellCard, User } from '@/models'

export default {
  mixins: [
    UsesAuthenticatedUser,
    UsesCurrency
  ],
  props: {
    /**
     * The card data. This should be coming directly from the API via the KintellCard model
     */
    card: {
      required: true,
      type: KintellCard,
    },
    /**
     * The user who created the kintell card. This should be on the KintellCard model, but for performance reasons we
     * request it manually
     */
    advisor: {
      type: User,
      required: true
    },
    /**
     * Whether to display the edit button at the bottom of the widget
     * Also shows chip details
     */
    editDetails: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to display the share button at the bottom of the widget
     */
    shareButton: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to display the remove favourite button at the bottom of the widget
     */
    removeButton: {
      type: Boolean,
      default: false
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    hideReviews: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false
    },
    /** Hides the skills and centers the rate */
    minimal: Boolean,
    hideFavourite: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      userIcon: require('~images/user-grey.svg'),
      showShareModal: false,
      loadingShortLink: false
    }
  },
  computed: {
    classes () {
      return {
        'k-kintell-card-widget--small': this.small,
        'k-kintell-card-widget--minimal': this.minimal
      }
    },
    /**
     * For small cards we only show the first 3 skills, otherwise we show all of them
     */
    skills () {
      return take(this.card.skills, 3)
    },
    shouldSeeCardStatus () {
      return this.editDetails && this.card.friendlyStatus !== 'Published' && this.card.status
    },
    hasFooterButtons () {
      return this.editDetails || this.shareButton || this.removeButton
    }
  },
  watch: {
    async showShareModal (value) {
      if (value === true && !this.card.shortLink && (this.authenticatedUser.id === this.card.createdBy)) {
        this.loadingShortLink = true
        await this.card.fetchShortLink()
        this.loadingShortLink = false
      }
    }
  },
  methods: {
    removeButtonClicked () {
      this.$emit('removed')
    }
  },
}
</script>

<style lang="scss" scoped>
.k-kintell-card-widget {
  margin: 0 auto;
  $root: &;

  .k-card {
    display: flex;
    flex-direction: column;
  }

  &__content {
    flex: 1;
    flex-grow: 2;
  }

  &__avatar {
    box-shadow: 0 5px 15px rgba(0, 0, 0, .07), 0 15px 35px rgba(50, 50, 93, .1);
    margin-bottom: 15px;

    .v-image {
      width: 100%;
    }
  }

  &__actions {
    background: #fbfbfb;
    border-top: 1px solid;
    border-top-color: #342d2f05;
  }

  &__rate {
    @include typography-base(paragraph--semi-medium);
  }

  &__video {
    position: absolute;
    left: 21px;
    top: 21px;

    @include xs-only {
      left: 10px;
      top: 10px;
      height: 20px !important;
      font-size: 20px !important;
    }
  }

  &__favourite {
    position: absolute;
    right: 15px;
    top: 20px;

    #{$root}--minimal & {
      right: 8px;
      top: 8px;
    }
  }

  &__title {
    @include sm-and-up {
      line-height: 24px;
      font-size: 17px;
    }
  }

  .k-kintell-card-skill-list {
    ::v-deep {
      .small-caps--small {
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
}

.skill-container {
  min-height: 44px;
}
</style>
